// Day report for a realtime project: pure, deterministic calculations from the
// hourly presence and country rows the API already serves. No text is generated
// by a model — every sentence is a template filled from these numbers, so the
// same data always produces the same report. Kept free of React and fetch so the
// same functions can run in a Node script when reports are automated.

import type { RealtimeRow, RealtimeCountryRow } from "../services/realtimeApi";
import { DOMESTIC } from "./realtimeCountry";
import { deductFloor } from "../realtime/zoneReadings";

export const REPORT_TZ = "Europe/Oslo";
const REST = "REST"; // rest-of-world bucket in the country data

// ---------- Oslo calendar days ----------

/** Minutes Oslo is ahead of UTC at a given instant (DST-aware). */
function osloOffsetMinutes(utcMs: number): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: REPORT_TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(utcMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
  return Math.round((asUtc - utcMs) / 60000);
}

/** UTC instant at which an Oslo calendar day (YYYY-MM-DD) starts. */
function osloDayStart(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  const midnightUtc = Date.UTC(y, m - 1, d);
  return midnightUtc - osloOffsetMinutes(midnightUtc) * 60000;
}

/** [from, to) in UTC ms for an Oslo calendar day — 23 or 25 hours on DST days. */
export function osloDayRange(date: string): { from: number; to: number } {
  const [y, m, d] = date.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return { from: osloDayStart(date), to: osloDayStart(next) };
}

/** Today's date in Oslo, YYYY-MM-DD. */
export function osloToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: REPORT_TZ }).format(new Date());
}

const hourFmt = new Intl.DateTimeFormat("en-GB", { timeZone: REPORT_TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
/** "14:00" for an hour that starts at `ms`. */
export const hourLabel = (ms: number) => hourFmt.format(new Date(ms));
/** "14:00–15:00" */
export const hourRange = (ms: number) => `${hourLabel(ms)}–${hourLabel(ms + 3600000)}`;
/** "Wed 9 Sep 2026" — built from parts, since en-GB formats it as "Wed, 9 Sept 2026". */
export const dayLabel = (date: string) => {
  const d = new Date(`${date}T12:00:00Z`);
  const wd = d.toLocaleDateString("en-GB", { timeZone: "UTC", weekday: "short" });
  const mo = d.toLocaleDateString("en-GB", { timeZone: "UTC", month: "short" }).slice(0, 3);
  return `${wd} ${d.getUTCDate()} ${mo} ${d.getUTCFullYear()}`;
};

// ---------- the report model ----------

export interface ReportHour {
  start: number; // UTC ms of the hour's start
  present: number | null; // sum over zones of the hourly mean present
  intl: number | null; // sum over zones of the hourly mean international
  countryTotal: number | null; // everyone in the country data that hour (share denominator)
}

export interface Peak {
  value: number;
  start: number;
}

export interface ReportFigures {
  peakPresent: Peak | null;
  avgPresent: number | null; // mean of the hourly totals over hours with data
  peakIntl: Peak | null;
  intlShareAtPeak: number | null; // 0..1, from the country data alone (self-consistent)
}

export interface ReportZone {
  id: string;
  name: string;
  peak: Peak;
  intlShare: number | null; // 0..1 over the day
}

export interface ReportCountry {
  country: string;
  avg: number; // average present over the day's hours with data
}

export interface ReportGroup extends ReportFigures {
  id: string;
  name: string;
  zones: number;
}

export interface ReportDay extends ReportFigures {
  date: string;
  zonesWithPresence: number;
  zonesWithIntl: number;
  hours: ReportHour[];
  topZones: ReportZone[];
  topCountries: ReportCountry[];
  groups: ReportGroup[];
}

export interface ReportGroupInput {
  id: string;
  name: string;
  areaIds: string[];
}

const maxPeak = (hours: ReportHour[], key: "present" | "intl"): Peak | null => {
  let best: Peak | null = null;
  for (const h of hours) {
    const v = h[key];
    if (v != null && (!best || v > best.value)) best = { value: v, start: h.start };
  }
  return best;
};

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

/** Hour-by-hour totals for a set of zones (null = every zone). */
function hoursFor(
  date: string,
  presence: RealtimeRow[],
  country: RealtimeCountryRow[],
  scope: Set<string> | null
): ReportHour[] {
  const { from, to } = osloDayRange(date);
  const hours: ReportHour[] = [];
  const index = new Map<number, ReportHour>();
  for (let t = from; t < to; t += 3600000) {
    const h: ReportHour = { start: t, present: null, intl: null, countryTotal: null };
    hours.push(h);
    index.set(t, h);
  }
  const at = (ts: string) => index.get(Math.floor(+new Date(ts) / 3600000) * 3600000);
  for (const r of presence) {
    if (scope && !scope.has(r.target_id)) continue;
    const h = at(r.timestamp);
    if (h) h.present = (h.present ?? 0) + r.subscriber_count;
  }
  for (const r of country) {
    if (scope && !scope.has(r.target_id)) continue;
    const h = at(r.timestamp);
    if (!h) continue;
    h.countryTotal = (h.countryTotal ?? 0) + r.subscriber_count;
    if (r.country !== DOMESTIC) h.intl = (h.intl ?? 0) + r.subscriber_count;
    else if (h.intl == null) h.intl = 0;
  }
  return hours;
}

function figuresFrom(hours: ReportHour[]): ReportFigures {
  const peakPresent = maxPeak(hours, "present");
  const peakIntl = maxPeak(hours, "intl");
  const atPeak = peakIntl ? hours.find((h) => h.start === peakIntl.start) : undefined;
  return {
    peakPresent,
    avgPresent: mean(hours.flatMap((h) => (h.present != null ? [h.present] : []))),
    peakIntl,
    intlShareAtPeak: atPeak && atPeak.countryTotal ? (atPeak.intl ?? 0) / atPeak.countryTotal : null,
  };
}

/**
 * Everything the report shows for one Oslo day. `presence` and `country` are
 * hourly-bucketed rows covering that day; `zoneIds` is the project's areas.
 */
export function buildReportDay(
  date: string,
  presence: RealtimeRow[],
  country: RealtimeCountryRow[],
  zoneIds: string[],
  groups: ReportGroupInput[],
  topN = 10
): ReportDay {
  const scope = new Set(zoneIds);
  const p = presence.filter((r) => scope.has(r.target_id));
  const c = country.filter((r) => scope.has(r.target_id));
  const hours = hoursFor(date, p, c, null);

  // Per zone: its own busiest hour, and its international share over the day.
  const names = new Map<string, string>();
  const zoneHours = new Map<string, Map<number, number>>();
  for (const r of p) {
    names.set(r.target_id, r.target_name);
    const t = Math.floor(+new Date(r.timestamp) / 3600000) * 3600000;
    const m = zoneHours.get(r.target_id) ?? new Map<number, number>();
    m.set(t, (m.get(t) ?? 0) + r.subscriber_count);
    zoneHours.set(r.target_id, m);
  }
  const zoneCountry = new Map<string, { intl: number; total: number }>();
  for (const r of c) {
    names.set(r.target_id, names.get(r.target_id) ?? r.target_name);
    const z = zoneCountry.get(r.target_id) ?? { intl: 0, total: 0 };
    z.total += r.subscriber_count;
    if (r.country !== DOMESTIC) z.intl += r.subscriber_count;
    zoneCountry.set(r.target_id, z);
  }
  const topZones: ReportZone[] = Array.from(zoneHours.entries())
    .map(([id, m]) => {
      let peak: Peak = { value: -1, start: 0 };
      m.forEach((v, t) => {
        if (v > peak.value) peak = { value: v, start: t };
      });
      const zc = zoneCountry.get(id);
      return { id, name: names.get(id) || id, peak, intlShare: zc && zc.total ? zc.intl / zc.total : null };
    })
    .sort((a, b) => b.peak.value - a.peak.value)
    .slice(0, topN);

  // Source countries: average present per country over the hours with data.
  const perCountryHour = new Map<string, Map<number, number>>();
  for (const r of c) {
    // REST is the operator's rest-of-world bucket: counted in the international
    // totals, but it isn't a country, so it stays out of this ranking.
    if (r.country === DOMESTIC || r.country === REST) continue;
    const t = Math.floor(+new Date(r.timestamp) / 3600000) * 3600000;
    const m = perCountryHour.get(r.country) ?? new Map<number, number>();
    m.set(t, (m.get(t) ?? 0) + r.subscriber_count);
    perCountryHour.set(r.country, m);
  }
  const hoursWithCountry = hours.filter((h) => h.countryTotal != null).length || 1;
  const topCountries: ReportCountry[] = Array.from(perCountryHour.entries())
    .map(([country, m]) => ({ country, avg: Array.from(m.values()).reduce((a, b) => a + b, 0) / hoursWithCountry }))
    .sort((a, b) => b.avg - a.avg)
    .slice(0, topN);

  return {
    date,
    ...figuresFrom(hours),
    zonesWithPresence: zoneHours.size,
    zonesWithIntl: zoneCountry.size,
    hours,
    topZones,
    topCountries,
    groups: groups
      .filter((g) => g.areaIds.length)
      .map((g) => ({
        id: g.id,
        name: g.name,
        zones: g.areaIds.length,
        ...figuresFrom(hoursFor(date, p, c, new Set(g.areaIds))),
      })),
  };
}

// ---------- comparisons and sentences ----------

/** Relative change from `from` to `to`, or null when either side is missing. */
export const pctChange = (to: number | null | undefined, from: number | null | undefined) =>
  to != null && from != null && from !== 0 ? (to - from) / from : null;

const n = (v: number) => Math.round(v).toLocaleString();
const pct = (v: number) => `${Math.round(v * 100)}%`;
const signedPct = (v: number) => {
  const r = Math.round(v * 100); // sign from the rounded value, so −0.2% reads ±0%
  return `${r > 0 ? "+" : r < 0 ? "−" : "±"}${Math.abs(r)}%`;
};

/** Summary sentences, each a fixed template filled from the figures. */
export function reportSentences(day: ReportDay, comparisons: ReportDay[]): string[] {
  const out: string[] = [];
  if (day.peakPresent) {
    out.push(
      `The busiest hour was ${hourRange(day.peakPresent.start)}, with on average ${n(day.peakPresent.value)} people present across ${day.zonesWithPresence} zones` +
        (day.avgPresent != null ? `; the hourly average over the day was ${n(day.avgPresent)}.` : ".")
    );
  } else {
    out.push("There is no presence data for this day.");
  }
  if (day.peakIntl) {
    out.push(
      `International presence was highest at ${hourRange(day.peakIntl.start)}, with ${n(day.peakIntl.value)} people` +
        (day.intlShareAtPeak != null ? ` — ${pct(day.intlShareAtPeak)} of everyone counted that hour.` : ".")
    );
  } else {
    out.push("There is no country breakdown for this day.");
  }
  const z = day.topZones[0];
  if (z) out.push(`${displayZoneName(z.name)} was the busiest zone, reaching ${n(z.peak.value)} at ${hourRange(z.peak.start)}.`);
  const cs = day.topCountries.slice(0, 3).map((c) => withArticle(c.country));
  if (cs.length) out.push(`The largest international groups came from ${listJoin(cs)}.`);
  for (const cmp of comparisons) {
    const parts: string[] = [];
    const a = pctChange(day.peakPresent?.value, cmp.peakPresent?.value);
    const b = pctChange(day.avgPresent, cmp.avgPresent);
    const c = pctChange(day.peakIntl?.value, cmp.peakIntl?.value);
    if (a != null) parts.push(`peak presence ${signedPct(a)}`);
    if (b != null) parts.push(`daily average ${signedPct(b)}`);
    if (c != null) parts.push(`international peak ${signedPct(c)}`);
    out.push(
      parts.length
        ? `Compared with ${dayLabel(cmp.date)}: ${listJoin(parts)}.`
        : `${dayLabel(cmp.date)} has no data to compare with.`
    );
  }
  return out;
}

const listJoin = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

// Country names that take "the" in running text.
const THE = new Set(["NL", "US", "GB", "AE", "CZ", "PH", "DO", "BS", "GM", "MV", "VA"]);
const withArticle = (code: string) => (THE.has(code) ? `the ${countryName(code)}` : countryName(code));

let regionNames: Intl.DisplayNames | null = null;
/** "DE" → "Germany"; falls back to the code. */
export function countryName(code: string): string {
  try {
    if (code === REST) return "Other countries";
    regionNames = regionNames ?? new Intl.DisplayNames(["en"], { type: "region" });
    return (code && code.length === 2 && regionNames.of(code)) || code;
  } catch {
    return code;
  }
}

// ---------- per-zone analyses (same maths as the 9 September report) ----------

export type ZoneHours = Map<string, { name: string; h: Map<number, number> }>;

/** Hourly mean per zone, keyed by the hour's start (UTC ms). */
export function zoneHourly(presence: RealtimeRow[], zoneIds: string[]): ZoneHours {
  const scope = new Set(zoneIds);
  const out: ZoneHours = new Map();
  for (const r of presence) {
    if (!scope.has(r.target_id)) continue;
    const z = out.get(r.target_id) ?? { name: r.target_name, h: new Map<number, number>() };
    const t = Math.floor(+new Date(r.timestamp) / 3600000) * 3600000;
    z.h.set(t, (z.h.get(t) ?? 0) + r.subscriber_count);
    out.set(r.target_id, z);
  }
  return out;
}

const avgOf = (m: Map<number, number>) => {
  const v = Array.from(m.values());
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

/** The same Oslo clock hour on another day (DST-safe: offset from each day's start). */
export const sameHourOn = (start: number, date: string, otherDate: string) =>
  osloDayRange(otherDate).from + (start - osloDayRange(date).from);

export interface ZoneChange {
  id: string;
  name: string;
  avg: number;
  avgChange: number | null; // vs the comparison day
  atHour: number | null; // at the main day's busiest hour
  atHourChange: number | null; // vs the same clock hour on the comparison day
}

/**
 * Zones ranked by how much their day average rose against a comparison day —
 * the view that shows where an event happened, rather than which districts are
 * simply large. Zones below `minAvg` are left out: small bases make wild %.
 */
export function zoneChanges(
  main: { date: string; zones: ZoneHours; busiestHour: number | null },
  cmp: { date: string; zones: ZoneHours },
  minAvg = 1000,
  topN = 10
): ZoneChange[] {
  const out: ZoneChange[] = [];
  main.zones.forEach((z, id) => {
    const c = cmp.zones.get(id);
    const avg = avgOf(z.h);
    if (avg == null || avg < minAvg) return;
    const atHour = main.busiestHour != null ? z.h.get(main.busiestHour) ?? null : null;
    const atHourCmp =
      main.busiestHour != null && c ? c.h.get(sameHourOn(main.busiestHour, main.date, cmp.date)) ?? null : null;
    out.push({
      id,
      name: z.name,
      avg,
      avgChange: c ? pctChange(avg, avgOf(c.h)) : null,
      atHour,
      atHourChange: pctChange(atHour, atHourCmp),
    });
  });
  return out
    .filter((z) => z.avgChange != null)
    .sort((a, b) => (b.avgChange as number) - (a.avgChange as number))
    .slice(0, topN);
}

/**
 * Hour-by-hour people above each zone's night-time floor: per zone
 * max(0, hourly mean − floor), summed. Zones without a floor are left out of
 * the sum rather than counted raw, so the series never mixes the two.
 */
export function aboveFloor(day: ReportDay, zones: ZoneHours, floors: Record<string, number>): (number | null)[] {
  return day.hours.map((hr) => {
    let total = 0;
    let any = false;
    zones.forEach((z, id) => {
      const v = z.h.get(hr.start);
      const f = floors[id];
      if (v == null || f == null) return;
      any = true;
      total += deductFloor(v, f);
    });
    return any ? total : null;
  });
}

export const peakOfSeries = (xs: (number | null)[], hours: ReportHour[]): Peak | null =>
  xs.reduce<Peak | null>((b, v, i) => (v != null && (!b || v > b.value) ? { value: v, start: hours[i].start } : b), null);

export const meanOfSeries = (xs: (number | null)[]) => mean(xs.filter((v): v is number => v != null));

/** "004_nordre_aker" → "Nordre Aker": the project prefix and underscores are storage detail. */
export const displayZoneName = (s: string) =>
  s
    .replace(/^\d+_/, "")
    .split("_")
    .map((w) => (w === w.toUpperCase() ? w : w[0].toUpperCase() + w.slice(1)))
    .join(" ");

/** Extra templated sentences for the per-zone analyses. */
export function extraSentences(opts: {
  main: ReportDay;
  cmpDate?: string;
  rises?: ZoneChange[];
  above?: { main: (number | null)[]; cmp?: (number | null)[]; cmpHours?: ReportHour[]; floorSum: number };
}): string[] {
  const out: string[] = [];
  const { main, cmpDate, rises, above } = opts;
  if (above) {
    const p = peakOfSeries(above.main, main.hours);
    const pc = above.cmp && above.cmpHours ? peakOfSeries(above.cmp, above.cmpHours) : null;
    const a = meanOfSeries(above.main);
    const ac = above.cmp ? meanOfSeries(above.cmp) : null;
    if (p) {
      let s = `With each zone's normal night-time level removed (${n(above.floorSum)} people in total), the busiest hour was ${hourRange(p.start)} with ${n(p.value)} people above baseline`;
      if (pc && cmpDate) s += `, against ${n(pc.value)} at ${hourRange(pc.start)} on ${dayLabel(cmpDate)} (${signedPct(pctChange(p.value, pc.value) as number)})`;
      if (a != null && ac != null) s += `; over the day, ${n(a)} against ${n(ac)} (${signedPct(pctChange(a, ac) as number)})`;
      out.push(s + ".");
    }
  }
  const r = rises?.[0];
  // Only a real rise is reported as one (rounded, so +0% doesn't count).
  if (r && r.avgChange != null && Math.round(r.avgChange * 100) > 0 && cmpDate) {
    out.push(
      `The largest rise was in ${displayZoneName(r.name)}: its day average was ${signedPct(r.avgChange)} against ${dayLabel(cmpDate)}` +
        (r.atHourChange != null && main.peakPresent ? `, and ${signedPct(r.atHourChange)} at ${hourRange(main.peakPresent.start)}.` : ".")
    );
  }
  return out;
}
